import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
} from '@mui/material';
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import RefreshIcon from '@mui/icons-material/Refresh';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import SchoolIcon from '@mui/icons-material/School';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import billingService, { type UserSubscriptionStatus, type Subscription } from '../services/billingService';
import { getApiErrorInfo } from '../services/api';
import { IndividualPlanSelectionPanel } from '../pages/billing/IndividualPlans';
import ActiveModuleManager from './billing/ActiveModuleManager';
import { useUserContext } from '../hooks/useUserContext';
import { config } from '../config';

const DEFAULT_TENANT_ID = config.defaultTenantId;

// 5-step type scale (12 / 14 / 18 / 28 / 36px) with explicit role tokens.
// Matches the .impeccable.md "premium professional" brief: fewer sizes, stronger contrast.
const TYPE = {
  pageTitle: { fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 },
  display:   { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
  subheading:{ fontSize: '1.125rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
  bodyStrong:{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
  body:      { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
  caption:   { fontSize: '0.75rem',  fontWeight: 500, lineHeight: 1.4 },
  overline:  { fontSize: '0.75rem',  fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, lineHeight: 1.4 },
} as const;

/** Map raw addon IDs to human-readable labels + icons */
const ADDON_DISPLAY: Record<string, { label: string; icon: typeof SchoolIcon }> = {
  'OnDemand-Module-Yearly': { label: 'On-Demand Courses', icon: SchoolIcon },
  'Webinars-Module-Yearly': { label: 'Live Webinars', icon: VideoLibraryIcon },
  'Enterprise-OnDemand-Module-Yearly': { label: 'On-Demand Courses', icon: SchoolIcon },
  'Enterprise-Webinar-Module-Yearly': { label: 'Live Webinars', icon: VideoLibraryIcon },
};

function formatAddonLabel(id: string): string {
  return ADDON_DISPLAY[id]?.label ?? id.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function getAddonIcon(id: string) {
  return ADDON_DISPLAY[id]?.icon ?? SchoolIcon;
}

/** Normalize billingPeriod values like "1", "month", "year" into readable text */
function formatBillingCycle(billingPeriod: string, billingPeriodUnit?: number): string {
  const p = billingPeriod.toLowerCase();
  if (p === 'year' || p === 'yearly' || p === 'annual') return 'year';
  if (p === 'month' || p === 'monthly') return 'month';
  // Chargebee sometimes returns just the unit count — fall back to "year" for annual plans
  if (billingPeriodUnit === 1 && (p === '1' || p === '12')) return p === '12' ? 'year' : 'year';
  if (p === '1') return 'year';
  return billingPeriod;
}

export default function IndividualBillingManagement() {
  const { backendProfile } = useUserContext();
  const isOrgMember = !!backendProfile?.tenantId && backendProfile.tenantId !== DEFAULT_TENANT_ID;

  const [status, setStatus] = useState<UserSubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadData(false);
  }, []);

  const loadData = async (syncFirst = false) => {
    try {
      setLoading(true);
      setError(null);
      const statusData = syncFirst
        ? await billingService.syncUserSubscription()
        : await billingService.getUserSubscriptionStatus();
      setStatus(statusData);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      if (apiError.isNetworkError) {
        setError('Unable to connect to the service. Please try again.');
      } else {
        setError(apiError.message || 'Failed to load billing information. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setActionLoading(true);
      await billingService.reactivateUserSubscription();
      await loadData();
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setActionLoading(true);
      await billingService.cancelUserSubscription({ endOfTerm: true });
      setCancelDialogOpen(false);
      await loadData();
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      setActionLoading(true);
      const session = await billingService.getUserBillingPortalSession();
      window.open(session.portalUrl, '_blank', 'noopener,noreferrer');
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to open billing portal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckout = async (planId: string, addonIds: string[], _billingPeriod: 'monthly' | 'annual') => {
    try {
      setActionLoading(true);
      sessionStorage.setItem('checkout_flow', 'billing');
      const redirectUrl = `${window.location.origin}/subscription-confirmation`;
      const page = await billingService.createUserSubscriptionCheckout({ planId, addonIds, redirectUrl });
      window.location.href = page.url;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to create checkout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleModuleChanges = async (addonIds: string[], _seatCounts: Record<string, number>) => {
    try {
      setActionLoading(true);
      sessionStorage.setItem('checkout_flow', 'billing');
      const redirectUrl = `${window.location.origin}/subscription-confirmation`;
      const page = await billingService.createUserManageCheckout({ addonIds, redirectUrl });
      window.location.href = page.url;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to create checkout');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (s: string): 'success' | 'warning' | 'default' | 'error' => {
    switch (s.toLowerCase()) {
      case 'active': return 'success';
      case 'cancelled': return 'error';
      case 'non_renewing': return 'warning'; // keep warning color
      case 'paused': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (s: string): string => {
    switch (s.toLowerCase()) {
      case 'active': return 'Active';
      case 'cancelled': return 'Expired';
      case 'non_renewing': return 'Auto-Renew Off';
      case 'paused': return 'Paused';
      default: return s.replace('_', ' ');
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const userSubscription: Subscription | undefined = status?.userSubscription;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', px: 3, py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={4} sx={{ px: { xs: 3, sm: 6, md: 10 }, py: 1, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Typography
        variant="h1"
        sx={{ ...TYPE.pageTitle, color: 'text.primary' }}
      >
        My Subscription
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Current Subscription ── */}
      {isOrgMember ? (
        <Alert severity="info" icon={<BusinessIcon />}>
          Your organization manages this subscription. Contact your admin with any questions about access or plan details.
        </Alert>
      ) : userSubscription ? (
        <Box
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.03)',
          }}
        >
          {/* Header — plan identity + status */}
          <Box sx={{ px: { xs: 2.5, sm: 4 }, pt: { xs: 3, sm: 4 }, pb: 3 }}>
            <Box>
              <Typography sx={{ ...TYPE.overline, color: 'primary.main', display: 'block', mb: 0.75 }}>
                Your Plan
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ ...TYPE.display, color: 'text.primary', wordBreak: 'break-word' }}>
                  {userSubscription.planName}
                </Typography>
                <Chip
                  label={getStatusLabel(userSubscription.status)}
                  color={getStatusColor(userSubscription.status)}
                  size="small"
                  variant={getStatusColor(userSubscription.status) === 'default' ? 'outlined' : 'filled'}
                  sx={{ ...TYPE.caption, fontWeight: 600, height: 24 }}
                />
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Details section */}
          <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: userSubscription.activeAddonIds?.length ? '1fr 1fr 1.2fr' : '1fr 1fr' },
                gap: { xs: 0, sm: 0 },
              }}
            >
              {/* Billing amount */}
              <Box sx={{ py: { xs: 0, sm: 0 }, pr: { sm: 3 } }}>
                <Typography sx={{ ...TYPE.overline, color: 'text.secondary' }}>
                  Billing
                </Typography>
                <Typography sx={{ ...TYPE.display, mt: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                  {formatCurrency(userSubscription.planAmount, userSubscription.currency)}
                </Typography>
                <Typography sx={{ ...TYPE.caption, color: 'text.secondary' }}>
                  per {formatBillingCycle(userSubscription.billingPeriod, userSubscription.billingPeriodUnit)}
                </Typography>
              </Box>

              {/* Mobile divider */}
              <Divider sx={{ my: 2, display: { xs: 'block', sm: 'none' } }} />

              {/* Next billing date */}
              <Box
                sx={{
                  py: { xs: 0, sm: 0 },
                  px: { sm: 3 },
                  borderLeft: { xs: 'none', sm: '1px solid' },
                  borderColor: { sm: 'divider' },
                }}
              >
                <Typography sx={{ ...TYPE.overline, color: 'text.secondary' }}>
                  Next Renewal
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                  <CalendarIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography sx={{ ...TYPE.bodyStrong, fontVariantNumeric: 'tabular-nums' }}>
                    {formatDate(userSubscription.nextBillingAt)}
                  </Typography>
                </Box>
                {userSubscription.status !== 'cancelled' && userSubscription.status !== 'non_renewing' && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<SyncDisabledIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={() => setCancelDialogOpen(true)}
                    disabled={actionLoading}
                    sx={{ ...TYPE.body, mt: 1, px: 0, color: 'text.secondary', whiteSpace: 'nowrap', textTransform: 'none', '&:hover': { color: 'warning.main', bgcolor: 'transparent' } }}
                  >
                    Turn off auto-renew
                  </Button>
                )}
                {userSubscription.status === 'non_renewing' && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<RefreshIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={handleReactivateSubscription}
                    disabled={actionLoading}
                    sx={{ ...TYPE.body, mt: 1, px: 0, color: 'text.secondary', whiteSpace: 'nowrap', textTransform: 'none', '&:hover': { color: 'success.main', bgcolor: 'transparent' } }}
                  >
                    Reactivate
                  </Button>
                )}
              </Box>

              {/* Mobile divider */}
              {userSubscription.activeAddonIds?.length > 0 && (
                <Divider sx={{ my: 2, display: { xs: 'block', sm: 'none' } }} />
              )}

              {/* Active modules */}
              {userSubscription.activeAddonIds?.length > 0 && (
                <Box
                  sx={{
                    py: { xs: 0, sm: 0 },
                    pl: { sm: 3 },
                    borderLeft: { xs: 'none', sm: '1px solid' },
                    borderColor: { sm: 'divider' },
                  }}
                >
                  <Typography sx={{ ...TYPE.overline, color: 'text.secondary' }}>
                    Active Modules
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                    {userSubscription.activeAddonIds.map(id => {
                      const Icon = getAddonIcon(id);
                      const isWebinar = id.toLowerCase().includes('webinar');
                      return (
                        <Chip
                          key={id}
                          icon={<Icon sx={{ fontSize: '16px !important' }} />}
                          label={formatAddonLabel(id)}
                          size="small"
                          sx={(theme) => {
                            const token = theme.palette.status[isWebinar ? 'webinar' : 'onDemand'];
                            const iconColor = isWebinar ? theme.palette.primary.main : theme.palette.success.main;
                            return {
                              ...TYPE.caption,
                              bgcolor: token.bg,
                              color: token.fg,
                              border: 'none',
                              '& .MuiChip-icon': { color: iconColor },
                            };
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Status warnings */}
          {userSubscription.status === 'non_renewing' && (
            <Box sx={{ px: { xs: 2.5, sm: 4 }, pb: 3 }}>
              <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                Auto-renew is off. You'll keep full access until{' '}
                <strong>{formatDate(userSubscription.currentTermEnd)}</strong>.
              </Alert>
            </Box>
          )}
          {userSubscription.status === 'cancelled' && (
            <Box sx={{ px: { xs: 2.5, sm: 4 }, pb: 3 }}>
              <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
                Your subscription expired. Choose modules below to restore access.
              </Alert>
            </Box>
          )}

          {/* Card footer action */}
          {userSubscription.status !== 'cancelled' && (
            <>
              <Divider />
              <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<CreditCardIcon sx={{ fontSize: '16px !important' }} />}
                  onClick={handleUpdatePaymentMethod}
                  disabled={actionLoading}
                  sx={{ ...TYPE.body, textTransform: 'none', color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'transparent' } }}
                >
                  Update billing information
                </Button>
              </Box>
            </>
          )}
        </Box>
      ) : (
        /* ── Empty state — no subscription ── */
        <Box
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.03)',
            px: { xs: 2.5, sm: 4 },
            py: { xs: 3, sm: 4 },
          }}
        >
          <Typography sx={{ ...TYPE.overline, color: 'text.secondary', display: 'block', mb: 0.75 }}>
            No active subscription
          </Typography>
          <Typography sx={{ ...TYPE.display, color: 'text.primary', mb: 1 }}>
            Get started with CPAx Academy
          </Typography>
          <Typography sx={{ ...TYPE.body, color: 'text.secondary', mb: 2.5, maxWidth: '60ch' }}>
            Annual plans, billed once a year. Cancel auto-renew anytime before your next renewal.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<SchoolIcon />}
              label="On-Demand Courses"
              size="small"
              sx={(theme) => ({
                ...TYPE.caption,
                bgcolor: theme.palette.status.onDemand.bg,
                color: theme.palette.status.onDemand.fg,
                border: 'none',
                '& .MuiChip-icon': { color: theme.palette.success.main },
              })}
            />
            <Chip
              icon={<VideoLibraryIcon />}
              label="Live Webinars"
              size="small"
              sx={(theme) => ({
                ...TYPE.caption,
                bgcolor: theme.palette.status.webinar.bg,
                color: theme.palette.status.webinar.fg,
                border: 'none',
                '& .MuiChip-icon': { color: theme.palette.primary.main },
              })}
            />
          </Box>
        </Box>
      )}

      {/* ── Plan Selection / Add-on Management ── */}
      {!isOrgMember && (
        <Box>
          <Typography
            sx={{
              ...TYPE.subheading,
              mb: userSubscription && userSubscription.status !== 'cancelled' ? 0.5 : 2.5,
            }}
          >
            {userSubscription && userSubscription.status !== 'cancelled'
              ? 'Your modules'
              : 'Choose your modules'}
          </Typography>
          {userSubscription && userSubscription.status !== 'cancelled' && (
            <Typography sx={{ ...TYPE.body, color: 'text.secondary', mb: 2.5, maxWidth: '60ch' }}>
              Add a module; changes prorate automatically and complete at secure checkout.
            </Typography>
          )}
          {userSubscription && userSubscription.status !== 'cancelled' ? (
            <ActiveModuleManager
              addonType="individual"
              activeAddonIds={userSubscription.activeAddonIds ?? []}
              onApplyChanges={handleModuleChanges}
              isApplying={actionLoading}
            />
          ) : (
            <IndividualPlanSelectionPanel onContinue={handleCheckout} />
          )}
        </Box>
      )}

      {/* ── Turn Off Auto-Renew Dialog ── */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle sx={TYPE.subheading}>Turn off auto-renew?</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...TYPE.body, mb: 2 }}>
            You'll keep full access until <strong>{formatDate(userSubscription?.currentTermEnd ?? userSubscription?.nextBillingAt)}</strong>. After that, your subscription expires and you won't be charged again.
          </Typography>
          <Alert severity="info" sx={{ '& .MuiAlert-message': TYPE.body }}>
            Need to remove a module or request a prorated refund? Email{' '}
            <a
              href="mailto:hello@cpacrossings.com?subject=Cpax%20Academy%20subscription%20change"
              style={{ color: 'inherit', fontWeight: 600 }}
            >
              hello@cpacrossings.com
            </a>
            .
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelSubscription}
            color="warning"
            variant="text"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Turn off auto-renew'}
          </Button>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            variant="contained"
            color="primary"
            disabled={actionLoading}
            autoFocus
          >
            Keep auto-renew
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
